import { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export default function SimpleBranchBar({ data }) {
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const chartData = data?.branchwiseAttendanceSummary
    ?.filter((item) => item.branch && item.branch.trim() !== "")
    ?.map((item) => ({
      name: item.branch,
      totalEmployees: item.totalEmployees,
      checkedInToday: item.checkedInToday,
      notCheckedInToday: item.notCheckedInToday,
      employeeDetails: item.employeeDetails || [],
    }));

  const gradients = [
    ["#ff9966", "#ff5e62"],
    ["#6eb54dff", "#a8e063"],
    ["#36D1DC", "#5B86E5"],
    ["#8E2DE2", "#4A00E0"],
    ["#56ab2f", "#a8e063"],
    ["#F7971E", "#FFD200"],
  ];

  const handleBarClick = (data) => {
    setSelectedBranch(data);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedBranch(null);
  };

  return (
    <Box
      sx={{
        width: "130%",
        height: isSmallScreen ? 300 : 400,
        p: { xs: 1, sm: 2 },
      }}
    >
      {/* ✅ Responsive Chart */}
      {chartData?.length > 0 ? (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 20, left: 10, bottom: 20 }}
          >
            <defs>
              {gradients.map((g, i) => (
                <linearGradient
                  key={i}
                  id={`color${i}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={g[0]} />
                  <stop offset="100%" stopColor={g[1]} />
                </linearGradient>
              ))}
            </defs>

            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              interval={0}
              tick={{ fill: "#555", fontSize: isSmallScreen ? 10 : 13 }}
              angle={isSmallScreen ? -30 : -20}
              textAnchor="end"
            />
            <YAxis
              domain={[0, "dataMax + 2"]}
              tick={{ fill: "#555", fontSize: isSmallScreen ? 10 : 13 }}
              label={{
                value: "Employees",
                angle: -90,
                position: "insideLeft",
                fill: "#666",
                fontSize: isSmallScreen ? 10 : 12,
              }}
            />
            <Tooltip cursor={{ fill: "rgba(0,0,0,0.05)" }} />

            <Bar
              dataKey="checkedInToday"
              name="Checked In"
              barSize={isSmallScreen ? 30 : 50}
              radius={[12, 12, 0, 0]}
              onClick={handleBarClick}
            >
              {chartData?.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={`url(#color${index % gradients.length})`}
                  style={{ cursor: "pointer" }}
                />
              ))}
              <LabelList
                dataKey="checkedInToday"
                position="top"
                style={{
                  fill: "#222",
                  fontWeight: "600",
                  fontSize: isSmallScreen ? 9 : 11,
                }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Typography textAlign="center" color="textSecondary">
          No branch data available.
        </Typography>
      )}

      {/* ✅ Branch Details Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 2 },
        }}
      >
        {selectedBranch && (
          <>
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#4A00E0",
                fontWeight: "bold",
              }}
            >
              {selectedBranch.name} — Branch Details
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Total Employees:</strong>{" "}
                {selectedBranch.totalEmployees} | <strong>Checked In:</strong>{" "}
                {selectedBranch.checkedInToday} |{" "}
                <strong>Not Checked In:</strong>{" "}
                {selectedBranch.notCheckedInToday}
              </Typography>

              {selectedBranch.employeeDetails.length > 0 ? (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f3ff" }}>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Emp ID
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Employee Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Check In
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Check Out
                        </TableCell>
                                   <TableCell sx={{ fontWeight: "bold" }}>
                      Total Working Hours
                        </TableCell>
                             <TableCell sx={{ fontWeight: "bold" }}>
                          OverTime 
                        </TableCell>
                  
                      </TableRow>

                      <TableBody>
                        {selectedBranch.employeeDetails.map((emp, index) => (
                          <TableRow key={index}>
                            <TableCell>{emp.empid}</TableCell>
                            <TableCell>{emp.employeeName}</TableCell>
                            <TableCell>{emp.checkIn}</TableCell>
                            <TableCell>{emp.checkOut}</TableCell>
                             <TableCell>{emp.totalWorkingHours}</TableCell>
                              <TableCell>{emp.overtime}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </TableHead>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">
                  No employee details available.
                </Typography>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
